"use client";

import React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import { Mail, Linkedin, Twitter } from "lucide-react";
import NextImage from "next/image";
import { useLanguage } from "@/context/LanguageContext";

/** Auto-play interval in milliseconds. */
const AUTOPLAY_INTERVAL_MS = 2500;

/**
 * Represents a single leadership team member.
 */
interface Leader {
  id: string;
  name: string;
  title: string;
  description: string;
  image: string;
}

/**
 * Props for the LeadershipCarousel component.
 */
interface LeadershipCarouselProps {
  leaders: Leader[];
}

/**
 * A responsive auto-play carousel component for displaying leadership team members.
 */
const LeadershipCarousel: React.FC<LeadershipCarouselProps> = React.memo(({ leaders }) => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  /** Updates the number of visible cards based on viewport width. */
  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth >= 1024) {
        setVisibleCount(3);
      } else if (window.innerWidth >= 768) {
        setVisibleCount(2);
      } else {
        setVisibleCount(1);
      }
    };

    let resizeTimer: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateVisibleCount, 150);
    };

    updateVisibleCount();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const maxIndex = Math.max(0, (leaders?.length || 0) - visibleCount);

  // Clamp currentIndex when visibleCount changes
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [maxIndex, currentIndex]);

  /** Auto-play: advances to the next slide or loops back to the start. */
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, AUTOPLAY_INTERVAL_MS);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [maxIndex]);

  /** Navigates to a specific slide by dot index, resetting auto-play timer. */
  const handleDotClick = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      // Reset timer so user has time to view the selected slide
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      timerRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      }, AUTOPLAY_INTERVAL_MS);
    },
    [maxIndex],
  );

  const totalDots = maxIndex + 1;

  return (
    <div className="relative">
      {/* Carousel Track */}
      <div className="overflow-hidden rounded-2xl">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * (100 / visibleCount)}%)`,
          }}
        >
          {leaders?.map((leader, index) => {
            const isActive =
              index >= currentIndex && index < currentIndex + visibleCount;
            return (
              <div
                key={leader.id}
                className={`min-w-0 shrink-0 grow-0 basis-full pl-4 transition-all duration-[800ms] md:basis-1/2 lg:basis-1/3 ${
                  isActive
                    ? "opacity-100 scale-100"
                    : "opacity-40 scale-95 blur-[2px]"
                }`}
              >
                <div className="group relative bg-[#0a192f]/60 backdrop-blur-xl rounded-[2.5rem] p-6 h-full border border-[#00f3ff]/20 hover:border-[#00f3ff]/50 transition-all duration-500 overflow-hidden hover:shadow-[0_0_30px_rgba(0,243,255,0.1)]">
                  {/* Decorative Tech Background */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#00f3ff]/5 rounded-bl-[100px] -z-10 transition-transform duration-500 group-hover:scale-110"></div>

                  <div className="relative mb-6 rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#00f3ff]/5">
                    <NextImage
                      alt={leader.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      src={leader.image}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
                    />
                    {/* Neon Tech Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#040d21] via-transparent to-transparent opacity-80"></div>

                    <div className="absolute bottom-4 left-4 right-4 flex gap-2 translate-y-12 group-hover:translate-y-0 transition-transform duration-500 opacity-0 group-hover:opacity-100">
                      <button className="flex-1 bg-[#0a192f]/80 backdrop-blur-md text-white hover:text-[#0077b5] py-2.5 rounded-xl border border-[#00f3ff]/30 hover:bg-[#0077b5]/20 transition-all flex items-center justify-center gap-2 group/btn">
                        <Linkedin className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                      </button>
                      <button className="flex-1 bg-[#0a192f]/80 backdrop-blur-md text-white hover:text-[#1DA1F2] py-2.5 rounded-xl border border-[#00f3ff]/30 hover:bg-[#1DA1F2]/20 transition-all flex items-center justify-center gap-2 group/btn">
                        <Twitter className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                      </button>
                      <button className="flex-1 bg-[#0a192f]/80 backdrop-blur-md text-white hover:text-[#EA4335] py-2.5 rounded-xl border border-[#00f3ff]/30 hover:bg-[#EA4335]/20 transition-all flex items-center justify-center gap-2 group/btn">
                        <Mail className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                      </button>
                    </div>
                  </div>

                  <div className="px-2 pb-2">
                    <h3 className="text-2xl font-black text-white group-hover:text-[#00f3ff] group-hover:drop-shadow-[0_0_5px_currentColor] transition-all mb-1">
                      {((t(`home.leadership.items.${leader.id}`) as any)?.name) ||
                        leader.name}
                    </h3>
                    <p className="text-[#00f3ff] font-bold text-sm tracking-widest uppercase mb-4 opacity-90 drop-shadow-[0_0_2px_currentColor]">
                      {((t(`home.leadership.items.${leader.id}`) as any)?.title) ||
                        leader.title}
                    </p>
                    <p className="text-slate-300 text-sm font-medium leading-relaxed line-clamp-3">
                      {((t(`home.leadership.items.${leader.id}`) as any)
                        ?.description) || leader.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dot Indicators */}
      {totalDots > 1 && (
        <div className="flex justify-center gap-2 mt-10">
          {Array.from({ length: totalDots }).map((_, i) => (
            <button
              key={i}
              aria-label={`${t("common.pagination.goToSlide") || "Go to slide"} ${i + 1}`}
              className={`transition-all duration-500 rounded-full h-2 md:h-2.5 cursor-pointer border border-[#00f3ff]/20 ${
                currentIndex === i
                  ? "w-8 md:w-12 bg-[#00f3ff] shadow-[0_0_10px_#00f3ff]"
                  : "w-2 md:w-2.5 bg-[#0a192f] hover:bg-[#00f3ff]/50"
              }`}
              onClick={() => handleDotClick(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

LeadershipCarousel.displayName = "LeadershipCarousel";

export default LeadershipCarousel;
